import React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  AUTH0_AUDIENCE,
  AUTH0_CONFIGURED,
  getAuth0RedirectUri,
} from "./config";
import { normalizeUserableType } from "./auth0UserableType";

/** @typedef {"google" | "facebook" | "github" | "linkedin" | "generic"} Auth0SocialVariant */

const socialButtonStyle = {
  padding: "10px 12px",
  background: "#ffffff",
  color: "#3c4043",
  border: "1px solid #dadce0",
  borderRadius: "6px",
  cursor: "pointer",
  width: "100%",
  fontWeight: "600",
  boxShadow: "0 1px 2px rgba(60, 64, 67, 0.08)",
};

function SocialIcon({ variant, size = 18 }) {
  const wrap = { display: "inline-flex", alignItems: "center", gap: 10 };
  switch (variant) {
    case "google":
      return (
        <span style={wrap}>
          <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
            <path
              fill="#FFC107"
              d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
            />
            <path
              fill="#FF3D00"
              d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
            />
            <path
              fill="#4CAF50"
              d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.86 11.86 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
            />
            <path
              fill="#1976D2"
              d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
            />
          </svg>
        </span>
      );
    case "facebook":
      return (
        <span style={wrap}>
          <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
            <path
              fill="#1877F2"
              d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"
            />
          </svg>
        </span>
      );
    case "github":
      return (
        <span style={wrap}>
          <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
            <path
              fill="#24292f"
              d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"
            />
          </svg>
        </span>
      );
    case "linkedin":
      return (
        <span style={wrap}>
          <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
            <path
              fill="#0A66C2"
              d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
            />
          </svg>
        </span>
      );
    default:
      return null;
  }
}

function Auth0LoginButtonInner({
  userableType,
  rememberMe,
  label,
  /** Auth0 connection name (e.g. google-oauth2, facebook, github, linkedin_oidc). */
  connection,
  /** Social button look + icon; omit for legacy purple (unused on login after social-only). */
  variant = "primary",
  /** `"icon"` = brand icon only (row tiles); use `aria-label` from `label`. */
  tileDisplay = "full",
}) {
  const { loginWithRedirect, isLoading } = useAuth0();

  const primaryStyle = {
    padding: "10px 12px",
    marginTop: "14px",
    background: "#635bff",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    cursor: isLoading ? "wait" : "pointer",
    width: "100%",
    fontWeight: "600",
    opacity: isLoading ? 0.75 : 1,
  };

  const isSocial = variant !== "primary";
  const isIconTile = isSocial && tileDisplay === "icon";
  const buttonStyle = isSocial
    ? isIconTile
      ? {
          ...socialButtonStyle,
          width: "auto",
          minWidth: 42,
          minHeight: 42,
          padding: "9px",
          borderRadius: "8px",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: isLoading ? "wait" : "pointer",
          opacity: isLoading ? 0.75 : 1,
        }
      : {
          ...socialButtonStyle,
          cursor: isLoading ? "wait" : "pointer",
          opacity: isLoading ? 0.75 : 1,
        }
    : primaryStyle;

  const handleClick = () => {
    const raw =
      userableType !== undefined && userableType !== null
        ? String(userableType).trim()
        : "";
    const portal = raw !== "" ? normalizeUserableType(userableType) : null;
    try {
      sessionStorage.setItem(
        "auth0_exchange_meta",
        JSON.stringify({
          ...(portal ? { exchangeUserableType: portal } : {}),
          rememberMe: !!rememberMe,
        })
      );
    } catch (_) {
      /* ignore */
    }
    loginWithRedirect({
      appState: {
        returnTo: "/auth-exchange",
        ...(portal ? { exchangeUserableType: portal } : {}),
        rememberMe: !!rememberMe,
      },
      authorizationParams: {
        audience: AUTH0_AUDIENCE,
        redirect_uri: getAuth0RedirectUri(),
        scope: "openid profile email offline_access",
        ...(connection ? { connection } : {}),
      },
    });
  };

  return (
    <div
      style={{
        width: isIconTile ? "auto" : "100%",
        marginTop: isSocial && !isIconTile ? 0 : isIconTile ? 0 : "8px",
      }}
    >
      <button
        type="button"
        disabled={isLoading}
        style={buttonStyle}
        onClick={handleClick}
        aria-label={isIconTile ? label : undefined}
      >
        {isSocial ? (
          isIconTile ? (
            variant !== "generic" ? (
              <SocialIcon variant={variant} size={22} />
            ) : (
              <span style={{ fontSize: 20, fontWeight: 700, color: "#5f6368" }} aria-hidden>
                ⋯
              </span>
            )
          ) : (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              {variant !== "generic" ? <SocialIcon variant={variant} /> : null}
              {label}
            </span>
          )
        ) : (
          label
        )}
      </button>
    </div>
  );
}

/**
 * Only mounts the inner component when Auth0 is fully configured, so `useAuth0`
 * never runs without `Auth0Provider` (see App.js).
 */
export default function Auth0LoginButton(props) {
  if (!AUTH0_CONFIGURED || !AUTH0_AUDIENCE) {
    return null;
  }
  return <Auth0LoginButtonInner {...props} />;
}
